import {assert} from 'Asserts';
import {DNA_COMPLEMENT_TABLE} from 'biojs/BioConstants';
import {TranslationAttributes} from 'biojs/Translation';
import * as biojsUtil from 'biojs/Util';
import * as CRISPRConstants from 'constants/CRISPRConstants';
import {CRISPRAnalysisSequenceAttributes, CRISPRGuide} from 'constants/CRISPRConstants';
import {StrandOfDoubleStranded} from 'constants/MolbioConstants';
import * as CRISPRUtil from 'molbio/crispr/CRISPRUtil';
import * as sequtil from 'molbio/SeqUtil';

const NULL_UPSTREAM_BASE = 'x';

function getEditScore(upstreamBase: string, position: number): number {
  if (upstreamBase === NULL_UPSTREAM_BASE) {
    return -1;
  }
  // Position is the 0-indexed position of the base within the guide sequence.
  //
  // These scores were taken from Figure 2B of
  // http://www.nature.com/nature/journal/v533/n7603/full/nature17946.html
  return {
    g: [0.2, 0.4, 0.5, 0.8, 4.6, 13.4, 17.0, 2.8, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    c: [0.5, 11.0, 5.7, 9.3, 20.3, 21.4, 22.4, 8.7, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    a: [0.8, 2.5, 5.6, 11.6, 21.9, 13.5, 19.0, 6.8, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    t: [4.1, 6.4, 16.0, 21.7, 24.2, 26.6, 25.2, 25.4, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
  }[upstreamBase.toLowerCase()]![position];
}

function getUpstreamBase(
  position: number,
  strand: StrandOfDoubleStranded | null,
  sequence: CRISPRAnalysisSequenceAttributes
): string {
  if ((position === 0 && strand === 1) || (position === sequence.length - 1 && strand === -1)) {
    return NULL_UPSTREAM_BASE;
  }
  return strand === 1
    ? sequence.bases[position - 1]
    : biojsUtil.revcomp(sequence.bases[position + 1], DNA_COMPLEMENT_TABLE);
}

export function getForwardStrandEditScores(
  guide: CRISPRGuide,
  sequence: CRISPRAnalysisSequenceAttributes
): Array<number> {
  const guideStart = CRISPRUtil.guideStart(guide, sequence.length);
  const scores = guide.bases.split('').map((base, guideIndex) => {
    if (base.toUpperCase() === CRISPRConstants.FORWARD_STRAND_EDITABLE_BASE) {
      const position =
        guide.strand === 1 ? guideStart + guideIndex : guideStart + guide.bases.length - guideIndex - 1;
      const upstreamBase = getUpstreamBase(position, guide.strand, sequence);
      return getEditScore(upstreamBase, guideIndex);
    }
    return -1;
  });
  if (guide.strand === -1) {
    return scores.reverse();
  }
  return scores;
}

export function getOverlappingTranslationsForGuide(
  guide: CRISPRGuide,
  sequence: CRISPRAnalysisSequenceAttributes
): Array<TranslationAttributes> {
  return sequence.translations.filter((t) =>
    sequtil.isRangeOverlap(
      CRISPRUtil.guideStart(guide, sequence.length),
      CRISPRUtil.guideEnd(guide, sequence.length),
      t.start,
      t.end
    )
  );
}

export function hasInvalidOverlappingTranslations(
  guide: CRISPRGuide,
  sequence: CRISPRAnalysisSequenceAttributes
): boolean {
  const overlappingTranslations = getOverlappingTranslationsForGuide(guide, sequence);
  if (overlappingTranslations.length === 0) {
    return false;
  }
  if (overlappingTranslations.length > 1) {
    return true;
  }
  const {strand, start, end} = overlappingTranslations[0];
  return strand === -1 || sequtil.isWrap(start, end);
}

export function getForwardStrandEditedBase(
  base: string,
  originalStrand: StrandOfDoubleStranded | null
): string {
  if (originalStrand === 1) {
    assert(
      base.toUpperCase() === CRISPRConstants.FORWARD_STRAND_EDITABLE_BASE,
      `Cannot edit base ${base} on forward strand.`
    );
  } else {
    assert(
      base.toUpperCase() ===
        biojsUtil.revcomp(CRISPRConstants.FORWARD_STRAND_EDITABLE_BASE, DNA_COMPLEMENT_TABLE),
      `Cannot edit base ${base} on reverse strand.`
    );
  }
  return {
    g: 'a',
    G: 'A',
    c: 't',
    C: 'T',
  }[base]!;
}
